import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Article, Category } from '../../types.ts';
import { useApp } from '../../context/AppContext.tsx';
import {
  X,
  Check,
  CheckCircle2,
  FileText,
  User,
  Globe,
  Eraser,
  Upload,
  Maximize2,
  Minimize2,
  ArrowLeft,
  Tag as TagIcon,
  BarChart2,
  Layers,
  Image as ImageIcon,
  HelpCircle,
  TrendingUp,
  Search,
  CheckCircle,
  AlertTriangle,
  Info,
  Link as LinkIcon,
  ExternalLink,
  Edit2,
} from 'lucide-react';
import { compressImageToWebP } from '../../utils/mediaOptimizer.ts';
import { handleImageError, getResilientFallbackImage } from '../../utils/imageUtils.ts';
import { RichTextEditor } from './RichTextEditor.tsx';

interface ArticleEditorProps {
  article: Partial<Article>;
  categories: Category[];
  onSave: (articleData: Partial<Article>) => void;
  onCancel: () => void;
  primaryColor?: string;
}

// Clean messy HTML (strips inline styles, nested tags, empty tags, and cleans up spans)
export function cleanAndSanitizeArticleHtml(html: string): string {
  if (!html) return '';
  let cleaned = html;

  // 1. Remove all inline style="..." or style='...' attributes from any HTML tags
  cleaned = cleaned.replace(/\s+style="[^"]*"/gi, '');
  cleaned = cleaned.replace(/\s+style='[^']*'/gi, '');

  // 2. Fix nested <h2><p>Text</p></h2> or <p><h2>Text</h2></p>
  cleaned = cleaned.replace(/<(h[1-6])>\s*<p\b[^>]*>(.*?)<\/p>\s*<\/\1>/gi, '<$1>$2</$1>');
  cleaned = cleaned.replace(/<p\b[^>]*>\s*<(h[1-6])\b[^>]*>(.*?)<\/\2>\s*<\/p>/gi, '<$1>$2</$1>');

  // 3. Remove isSelectedEnd classes or selection artifacts
  cleaned = cleaned.replace(/\s+class="isSelectedEnd"/gi, '');
  cleaned = cleaned.replace(/class=""/gi, '');

  // 4. Strip out empty or meaningless span tags to prevent massive nesting clutter
  cleaned = cleaned.replace(/<span[^>]*>(.*?)<\/span>/gi, '$1');

  // 5. Remove empty heading & paragraph tags
  cleaned = cleaned.replace(/<(h[1-6]|p|div|span)>\s*<\/\1>/gi, '');

  // 6. Remove empty nested paragraphs
  cleaned = cleaned.replace(/(<p>\s*<\/p>)+/gi, '');

  return cleaned.trim();
}

// Automatically detect headings that are too long (e.g. >180 chars) and convert them back to normal paragraphs
export function autoFixOversizedHeadings(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');

    let modified = false;
    headings.forEach((heading) => {
      const text = heading.textContent || '';
      if (text.trim().length > 180) {
        const p = doc.createElement('p');
        p.innerHTML = heading.innerHTML;
        heading.parentNode?.replaceChild(p, heading);
        modified = true;
      }
    });

    return modified ? doc.body.innerHTML : html;
  } catch {
    return html;
  }
}

export default function ArticleEditor({
  article,
  categories,
  onSave,
  onCancel,
  primaryColor = '#059669',
}: ArticleEditorProps) {
  const { showToast, user, users, articles, products, gallery: galleryItems } = useApp();

  // Form States:
  const [title, setTitle] = useState(article.title || '');
  const [slug, setSlug] = useState(article.slug || '');
  const [summary, setSummary] = useState(article.summary || '');
  const [categoryId, setCategoryId] = useState(article.categoryId || (categories[0]?.id || 'cat-1'));
  const [tags, setTags] = useState<string[]>(article.tags || []);
  const [tagUrls, setTagUrls] = useState<Record<string, string>>(article.tagUrls || {});
  const [tagInput, setTagInput] = useState('');
  const [tagUrlInput, setTagUrlInput] = useState('');
  const [editingTagUrl, setEditingTagUrl] = useState<{ tag: string; url: string } | null>(null);
  const [coverImage, setCoverImage] = useState(article.coverImage || '');
  const [imageCaption, setImageCaption] = useState(article.imageCaption || '');
  const [status, setStatus] = useState<'published' | 'draft'>(article.status || 'published');

  // UI States:
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [mediaImages, setMediaImages] = useState<string[]>([]);

  // Author Dropdown list & Custom toggle
  const [authorList, setAuthorList] = useState<string[]>([]);
  const [selectedAuthor, setSelectedAuthor] = useState<string>('');
  const [customAuthor, setCustomAuthor] = useState<string>('');
  const [isCustomAuthorActive, setIsCustomAuthorActive] = useState(false);

  // Suggested Tags (used > 10 times)
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [tagCounts, setTagCounts] = useState<Record<string, number>>({});

  // Naskah Editor Content State
  const [content, setContent] = useState(() => {
    let raw = article.content || '';
    raw = cleanAndSanitizeArticleHtml(raw);
    raw = autoFixOversizedHeadings(raw);
    return raw;
  });

  const [isLoadingFullContent, setIsLoadingFullContent] = useState(false);

  // Fetch full article content from server if editing an existing article whose content was omitted from lightweight list
  useEffect(() => {
    async function loadFullContent() {
      const targetIdOrSlug = article.id || article.slug;
      if (targetIdOrSlug && (!article.content || article.content.trim() === '')) {
        setIsLoadingFullContent(true);
        try {
          const res = await fetch(`/api/articles/${targetIdOrSlug}`);
          const json = await res.json();
          if (json.success && json.data) {
            const rawContent = json.data.content || '';
            const cleaned = autoFixOversizedHeadings(cleanAndSanitizeArticleHtml(rawContent));
            setContent(cleaned);
            if (json.data.summary) setSummary(json.data.summary);
            if (json.data.coverImage) setCoverImage(json.data.coverImage);
            if (json.data.title) setTitle(json.data.title);
            if (json.data.slug) setSlug(json.data.slug);
            if (json.data.tags) setTags(json.data.tags);
            if (json.data.imageCaption) setImageCaption(json.data.imageCaption);
            if (json.data.categoryId) setCategoryId(json.data.categoryId);
            if (json.data.status) setStatus(json.data.status);
          }
        } catch (err) {
          console.error('Failed to load full article content for editing:', err);
        } finally {
          setIsLoadingFullContent(false);
        }
      }
    }
    loadFullContent();
  }, [article.id, article.slug]);

  // Calculate Tag Counts & Authors strictly synchronized with registered users in dashboard
  useEffect(() => {
    const compiledAuthorsSet = new Set<string>();

    // Add registered user accounts created in the dashboard
    if (users && Array.isArray(users)) {
      users.forEach((u) => {
        if (u.name && u.name.trim()) compiledAuthorsSet.add(u.name.trim());
      });
    }

    if (user?.name && user.name.trim()) {
      compiledAuthorsSet.add(user.name.trim());
    }

    const registeredAuthors = Array.from(compiledAuthorsSet);
    if (registeredAuthors.length === 0) {
      registeredAuthors.push('Admin Utama');
    }
    setAuthorList(registeredAuthors);

    const initialAuthor = (article.author || user?.name || registeredAuthors[0] || '').trim();
    if (registeredAuthors.includes(initialAuthor)) {
      setSelectedAuthor(initialAuthor);
      setIsCustomAuthorActive(false);
      setCustomAuthor('');
    } else if (initialAuthor) {
      setSelectedAuthor('__CUSTOM__');
      setCustomAuthor(initialAuthor);
      setIsCustomAuthorActive(true);
    } else {
      setSelectedAuthor(registeredAuthors[0]);
      setIsCustomAuthorActive(false);
      setCustomAuthor('');
    }

    const counts: Record<string, number> = {};
    if (articles && Array.isArray(articles)) {
      articles.forEach((art) => {
        if (art.tags && Array.isArray(art.tags)) {
          art.tags.forEach((t) => {
            const cleanT = t.trim().replace(/^#/, '');
            if (cleanT) {
              counts[cleanT] = (counts[cleanT] || 0) + 1;
            }
          });
        }
      });
    }

    setTagCounts(counts);
    // Hanya cantumkan tagar yang sudah dipakai lebih dari 10 kali (> 10)
    const popular = Object.keys(counts)
      .filter((t) => counts[t] > 10)
      .sort((a, b) => counts[b] - counts[a]);
    setSuggestedTags(popular);

    const photosSet = new Set<string>();
    photosSet.add('https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1200');
    photosSet.add('https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1200');
    photosSet.add('https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=1200');
    photosSet.add('https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=1200');

    if (articles && Array.isArray(articles)) {
      articles.forEach((a) => {
        if (a.coverImage) photosSet.add(a.coverImage);
      });
    }
    if (products && Array.isArray(products)) {
      products.forEach((p) => {
        if (p.image) photosSet.add(p.image);
      });
    }
    if (galleryItems && Array.isArray(galleryItems)) {
      galleryItems.forEach((g) => {
        if (g.url && g.type === 'photo') photosSet.add(g.url);
      });
    }

    setMediaImages(Array.from(photosSet));
  }, [articles, products, galleryItems, user]);

  // Real-time SEO Analysis of Article Structure
  const seoStats = useMemo(() => {
    const textOnly = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const wordCount = textOnly ? textOnly.split(' ').filter(Boolean).length : 0;

    let h1Count = 0;
    let h2Count = 0;
    let h3Count = 0;
    let pCount = 0;

    if (typeof DOMParser !== 'undefined') {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        h1Count = doc.querySelectorAll('h1').length;
        h2Count = doc.querySelectorAll('h2').length;
        h3Count = doc.querySelectorAll('h3').length;
        pCount = doc.querySelectorAll('p').length;
      } catch {
        // fallback regex
        h1Count = (content.match(/<h1\b[^>]*>/gi) || []).length;
        h2Count = (content.match(/<h2\b[^>]*>/gi) || []).length;
        h3Count = (content.match(/<h3\b[^>]*>/gi) || []).length;
        pCount = (content.match(/<p\b[^>]*>/gi) || []).length;
      }
    }

    const hasTitle = Boolean(title.trim());
    const hasCover = Boolean(coverImage.trim());
    const hasTags = tags.length >= 2;
    const hasGoodWords = wordCount >= 300;
    const hasH2 = h2Count >= 1;

    let score = 0;
    if (hasTitle) score += 25;
    if (hasH2) score += 25;
    if (hasGoodWords) score += 20;
    if (hasCover) score += 15;
    if (hasTags) score += 15;

    return {
      wordCount,
      h1Count,
      h2Count,
      h3Count,
      pCount,
      score,
      isH1InBody: h1Count > 0,
    };
  }, [content, title, coverImage, tags]);

  // Auto-generate Slug
  useEffect(() => {
    if (!article.id && title && !slug) {
      const generatedSlug = title
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generatedSlug);
    }
  }, [title]);

  // Upload Local Cover File directly to server disk with WebP optimization
  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingCover(true);
    try {
      const res = await compressImageToWebP(file, 1200, 0.85);

      // Attempt to save file directly to backend /api/upload
      try {
        const formData = new FormData();
        const blob = await fetch(res.dataUrl).then((r) => r.blob());
        formData.append('image', blob, `article-cover-${Date.now()}.webp`);
        const uploadRes = await fetch('/api/upload', {
          method: 'POST',
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (uploadData.success && uploadData.url) {
          setCoverImage(uploadData.url);
          showToast('Gambar sampul artikel berhasil diunggah dan disimpan ke server!', 'success');
          return;
        }
      } catch (uploadErr) {
        console.warn('Direct upload failed, falling back to base64 Data URL:', uploadErr);
      }

      setCoverImage(res.dataUrl);
      showToast('Gambar sampul artikel berhasil disiapkan!', 'success');
    } catch {
      showToast('Gagal mengunggah gambar sampul', 'error');
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleAuthorSelection = (val: string) => {
    setSelectedAuthor(val);
    if (val === '__CUSTOM__') {
      setIsCustomAuthorActive(true);
    } else {
      setIsCustomAuthorActive(false);
      setCustomAuthor('');
    }
  };

  const handleTagInputChange = (val: string) => {
    if (val.includes(',')) {
      const parts = val.split(',');
      const newTags = [...tags];
      let added = false;

      parts.forEach((part, idx) => {
        if (idx === parts.length - 1 && !val.endsWith(',')) {
          setTagInput(part);
        } else {
          const clean = part.trim().replace(/^#/, '');
          if (clean && !newTags.includes(clean)) {
            newTags.push(clean);
            added = true;
          }
        }
      });

      if (val.endsWith(',')) {
        setTagInput('');
      }

      if (added) {
        setTags(newTags);
        showToast('Tagar berhasil ditambahkan!', 'success');
      }
    } else {
      setTagInput(val);
    }
  };

  const handleAddTagManual = () => {
    if (!tagInput.trim()) return;
    const cleanTags = tagInput
      .split(',')
      .map((t) => t.trim().replace(/^#/, ''))
      .filter(Boolean);

    const newTags = [...tags];
    const newTagUrls = { ...tagUrls };
    let added = false;

    cleanTags.forEach((t) => {
      if (!newTags.includes(t)) {
        newTags.push(t);
        added = true;
      }
      if (tagUrlInput.trim()) {
        newTagUrls[t] = tagUrlInput.trim();
      }
    });

    if (added) {
      setTags(newTags);
      setTagUrls(newTagUrls);
      showToast(tagUrlInput.trim() ? 'Tagar beserta tautan URL berhasil ditambahkan!' : 'Tagar berhasil ditambahkan!', 'success');
    }
    setTagInput('');
    setTagUrlInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
    const nextUrls = { ...tagUrls };
    delete nextUrls[tagToRemove];
    setTagUrls(nextUrls);
  };

  const handleSaveTagUrl = (tag: string, url: string) => {
    const nextUrls = { ...tagUrls };
    if (url.trim()) {
      nextUrls[tag] = url.trim();
      showToast(`Link URL untuk #${tag} berhasil disimpan!`, 'success');
    } else {
      delete nextUrls[tag];
      showToast(`Link URL untuk #${tag} dihapus (kembali ke default pencarian tag).`, 'info');
    }
    setTagUrls(nextUrls);
    setEditingTagUrl(null);
  };

  const handleSubmit = (targetStatus?: 'published' | 'draft') => {
    if (!title.trim()) {
      showToast('Judul artikel (Tag H1) wajib diisi!', 'error');
      return;
    }

    const editorValue = content;
    if (!editorValue.trim() || editorValue === '<p><br></p>') {
      showToast('Isi artikel tidak boleh kosong!', 'error');
      return;
    }

    let sanitizedHtml = cleanAndSanitizeArticleHtml(editorValue.trim());
    sanitizedHtml = autoFixOversizedHeadings(sanitizedHtml);

    const finalAuthor = isCustomAuthorActive ? customAuthor.trim() : selectedAuthor;
    if (!finalAuthor) {
      showToast('Nama penulis wajib dipilih atau diisi!', 'error');
      return;
    }

    // Resolve author avatar from registered dashboard users or profile
    const matchedAuthorUser = users?.find(
      (u) =>
        u.name.trim().toLowerCase() === finalAuthor.toLowerCase() ||
        u.username.trim().toLowerCase() === finalAuthor.toLowerCase() ||
        u.email.trim().toLowerCase() === finalAuthor.toLowerCase()
    );
    const resolvedAuthorAvatar =
      matchedAuthorUser?.avatar ||
      article.authorAvatar ||
      (user && (user.name === finalAuthor || user.username === finalAuthor) ? user.avatar : '') ||
      '';

    const finalStatus = targetStatus || status;
    const categoryObj = categories.find((c) => c.id === categoryId);
    const textOnly = sanitizedHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    const savedData: Partial<Article> = {
      ...article,
      title: title.trim(),
      slug: slug.trim() || title.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
      categoryId,
      categoryName: categoryObj?.name || 'Umum',
      author: finalAuthor,
      authorAvatar: resolvedAuthorAvatar,
      summary: summary.trim() || textOnly.substring(0, 160) + '...',
      content: sanitizedHtml,
      coverImage: coverImage.trim() || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1200',
      imageCaption: imageCaption.trim(),
      tags,
      tagUrls,
      status: finalStatus,
      isHeadline: article.isHeadline || false,
      isEditorPick: article.isEditorPick || false,
    };

    onSave(savedData);
  };

  return (
    <div
      className={`bg-slate-100 min-h-screen flex flex-col font-sans text-slate-900 ${
        isFullscreen
          ? 'fixed inset-0 z-50 rounded-none overflow-y-auto'
          : 'w-full rounded-2xl border border-slate-200 shadow-xl overflow-hidden'
      }`}
    >
      {/* TOP HEADER MENU */}
      <header className="bg-slate-900 text-white px-3 sm:px-6 py-3.5 flex items-center justify-between gap-3 sticky top-0 z-30 shadow-md">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors cursor-pointer shrink-0"
            title="Kembali ke Daftar Artikel"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm sm:text-base font-black tracking-tight text-white truncate">
                {article.id ? 'Edit Artikel & Struktur SEO' : 'Tulis Artikel Baru & Optimasi SEO'}
              </h2>
              <span
                className={`hidden sm:inline-block text-[10px] font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wider ${
                  status === 'published'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                }`}
              >
                {status === 'published' ? '✓ Diterbitkan' : '📄 Draft'}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors hidden md:block cursor-pointer"
            title={isFullscreen ? 'Keluar Layar Penuh' : 'Layar Penuh (Fullscreen Editor)'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          <button
            type="button"
            onClick={() => handleSubmit('draft')}
            className="px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold transition-colors border border-slate-700 cursor-pointer"
          >
            Simpan Draft
          </button>

          <button
            type="button"
            onClick={() => handleSubmit('published')}
            className="px-3.5 py-1.5 sm:px-5 sm:py-2 rounded-xl text-white font-extrabold text-xs shadow-md transition-all flex items-center gap-1.5 hover:brightness-110 active:scale-98 cursor-pointer"
            style={{ backgroundColor: primaryColor }}
          >
            <Check className="w-4 h-4" />
            <span>{article.id ? 'Perbarui Artikel' : 'Terbitkan Artikel'}</span>
          </button>
        </div>
      </header>

      {/* MAIN CONTAINER */}
      <div className="max-w-4xl w-full mx-auto p-3 sm:p-6 md:p-8 space-y-6 flex-1">
        
        {/* SEO PYRAMID STRUCTURE GUIDANCE BANNER */}
        <div className="bg-gradient-to-r from-blue-900 to-slate-900 text-white rounded-2xl p-4 sm:p-5 shadow-sm border border-blue-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-md bg-blue-500 text-white text-[10px] font-black uppercase tracking-wider flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                SEO Google Page 1 Ready
              </span>
              <span className="text-xs font-bold text-blue-200">Kaidah Struktur Heading Googlebot</span>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed max-w-2xl">
              Gunakan <strong>1x H1</strong> pada Judul Utama, lalu bagi naskah ke dalam <strong>H2</strong> untuk subjudul topik utama, dan <strong>H3</strong> untuk poin spesifik agar cepat terindeks dan masuk halaman 1 Google.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0 bg-white/10 p-2.5 rounded-xl border border-white/15">
            <div className="text-center px-2 border-r border-white/20">
              <span className="text-[10px] font-bold text-slate-300 block uppercase">Tag H1</span>
              <span className="text-sm font-black text-amber-300">1x (Judul)</span>
            </div>
            <div className="text-center px-2 border-r border-white/20">
              <span className="text-[10px] font-bold text-slate-300 block uppercase">Subjudul H2</span>
              <span className="text-sm font-black text-blue-300">{seoStats.h2Count}x</span>
            </div>
            <div className="text-center px-2">
              <span className="text-[10px] font-bold text-slate-300 block uppercase">Sub-topik H3</span>
              <span className="text-sm font-black text-white">{seoStats.h3Count}x</span>
            </div>
          </div>
        </div>

        {/* CARD 1: IDENTITAS (JUDUL H1, SLUG PERMALINK, PENULIS) */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 space-y-5 shadow-2xs">
          
          {/* 1. JUDUL UTAMA ARTIKEL (TAG H1 SEO) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">1</span>
                <span>Judul Utama Artikel</span>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 font-extrabold text-[10px] rounded-md border border-blue-200">
                  Tag SEO: H1 (Wajib & Utama)
                </span>
              </label>
              <span className="text-[11px] font-semibold text-slate-400">
                {title.length} karakter (Saran: 50-70 karakter)
              </span>
            </div>

            <textarea
              rows={2}
              placeholder="Contoh: 10 Rekomendasi Tempat Outbound & Gathering Terbaik di Lembang Bandung 2026..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-lg sm:text-2xl font-black text-slate-900 placeholder:text-slate-300 border-b-2 border-slate-300 focus:border-blue-600 pb-2 focus:outline-none transition-colors resize-y leading-tight font-sans"
              style={{ minHeight: '60px' }}
            />
            <p className="text-[11px] text-slate-500">
              *Googlebot menggunakan teks ini sebagai <strong>Tag H1 Utama</strong> dan judul tautan di hasil pencarian. Masukkan kata kunci target Anda di sini.
            </p>
          </div>

          {/* 2. SLUG PERMALINK */}
          <div className="space-y-1.5 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">2</span>
                <span>Slug URL / Permalink Ramah SEO</span>
                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-extrabold text-[10px] rounded-md border border-slate-200">
                  SEO Canonical
                </span>
              </label>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between sm:gap-3 text-xs">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 text-slate-600 font-mono w-full sm:flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-blue-700 font-bold shrink-0">
                  <Globe className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="text-slate-400 font-mono text-[11px] sm:text-xs break-all">
                    https://domain.com/artikel/
                  </span>
                </div>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="slug-url-artikel-ramah-seo"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 font-bold text-slate-900 text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const autoSlug = title
                    .toLowerCase()
                    .trim()
                    .replace(/[^\w\s-]/g, '')
                    .replace(/[\s_-]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                  setSlug(autoSlug);
                  showToast('Slug URL disinkronkan dari judul!', 'success');
                }}
                className="text-[11px] font-bold text-blue-700 hover:underline shrink-0 self-end sm:self-center cursor-pointer"
              >
                Sinkronkan
              </button>
            </div>
          </div>

          {/* 3. DROPDOWN PENULIS */}
          <div className="space-y-1.5 pt-3 border-t border-slate-100">
            <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">3</span>
              <span>Nama Penulis / Redaksi</span>
              <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-bold text-[10px] rounded-md">
                E-E-A-T Author Signal
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
              <div className="flex items-center gap-2">
                {(() => {
                  const currentAuthorName = isCustomAuthorActive ? customAuthor : selectedAuthor;
                  const matchedUser = users?.find(
                    (u) =>
                      u.name.trim().toLowerCase() === currentAuthorName.trim().toLowerCase() ||
                      u.username.trim().toLowerCase() === currentAuthorName.trim().toLowerCase()
                  );
                  const previewAvatar =
                    matchedUser?.avatar ||
                    article.authorAvatar ||
                    (user && (user.name === currentAuthorName || user.username === currentAuthorName) ? user.avatar : null) ||
                    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80';
                  return (
                    <img
                      src={previewAvatar}
                      alt={currentAuthorName || 'Penulis'}
                      className="w-8 h-8 rounded-full object-cover border border-slate-300 shrink-0 shadow-2xs"
                    />
                  );
                })()}
                <select
                  value={selectedAuthor}
                  onChange={(e) => handleAuthorSelection(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="" disabled>-- Pilih Penulis Yang Tersedia --</option>
                  {authorList.map((authName) => (
                    <option key={authName} value={authName}>
                      {authName}
                    </option>
                  ))}
                  <option value="__CUSTOM__">➕ Tulis Nama Baru (Manual)...</option>
                </select>
              </div>

              {isCustomAuthorActive && (
                <div className="animate-in slide-in-from-top-1 duration-200">
                  <input
                    type="text"
                    value={customAuthor}
                    onChange={(e) => setCustomAuthor(e.target.value)}
                    placeholder="Ketik Nama Penulis Baru Di Sini..."
                    className="w-full bg-blue-50/50 border-2 border-blue-200 focus:border-blue-600 rounded-xl px-3.5 py-2 font-bold text-slate-900 text-xs focus:bg-white focus:outline-none"
                  />
                </div>
              )}
            </div>
          </div>

        </div>

        {/* CARD 2: NASKAH ARTIKEL (RICH TEXT EDITOR DENGAN HEADING H1/H2/H3 AUTO-HIGHLIGHT) */}
        <div className="space-y-4 bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 shadow-2xs">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <label className="text-sm font-black text-slate-900 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">4</span>
                <span>Isi Lengkap Naskah Artikel & Struktur Heading</span>
                <span className="text-rose-500 font-bold">*</span>
              </label>
              <p className="text-xs text-slate-500 mt-0.5">
                Block kalimat lalu klik <strong>H2</strong> untuk subjudul artikel atau <strong>H3</strong> untuk sub-poin.
              </p>
            </div>

            {/* Live SEO Score Mini Badge */}
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs font-bold">
              <BarChart2 className="w-4 h-4 text-blue-600" />
              <span className="text-slate-600">Skor Struktur SEO:</span>
              <span className={`font-black ${seoStats.score >= 80 ? 'text-emerald-700' : 'text-amber-700'}`}>
                {seoStats.score}/100
              </span>
            </div>
          </div>

          {/* Heading Checklist & Diagnostic Alert if H1 inside body */}
          {seoStats.isH1InBody && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-2.5 text-xs text-amber-900">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <strong>Tips SEO Google:</strong> Ditemukan tag <code>&lt;H1&gt;</code> di dalam isi naskah. Standar SEO Google terbaik merekomendasikan hanya menggunakan <strong>1x H1</strong> pada Judul Utama di atas, sedangkan subjudul dalam naskah sebaiknya memakai <strong>H2</strong> dan <strong>H3</strong>.
              </div>
            </div>
          )}

          {/* Rich Text Editor Component */}
          <RichTextEditor
            value={content}
            onChange={(html) => setContent(html)}
            placeholder="Ketik atau tempel naskah artikel Anda di sini... Gunakan H2 untuk subjudul topik dan H3 untuk sub-poin."
            onSetCoverImage={(imgUrl) => {
              setCoverImage(imgUrl);
              showToast('Foto dari naskah berhasil dijadikan Gambar Sampul artikel!', 'success');
            }}
          />

          {/* EXCERPT RINGKASAN (SEO META DESCRIPTION) */}
          <div className="pt-3 border-t border-slate-100 space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <span>Ringkasan / Cuplikan Artikel (Excerpt)</span>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 font-extrabold text-[10px] rounded-md border border-blue-200">
                  Tag SEO: Meta Description
                </span>
              </label>
              <span className="text-[11px] font-semibold text-slate-400">
                {summary.length} karakter (Ideal: 120-160)
              </span>
            </div>
            <textarea
              rows={2}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Tulis 1-2 kalimat ringkasan yang menarik pengunjung dari Google Search..."
              className="w-full p-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-[11px] text-slate-500">
              *Teks ini muncul di bawah judul pada cuplikan hasil pencarian Google (Google Search Snippet).
            </p>
          </div>
        </div>

        {/* CARD 3: KATEGORI & TAGAR KATA KUNCI (SEO LSI) */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 space-y-5 shadow-2xs">
          
          {/* KATEGORI */}
          <div className="space-y-2">
            <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">5</span>
              <span>Kategori Artikel</span>
              <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-bold text-[10px] rounded-md">
                Topic Cluster SEO
              </span>
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategoryId(cat.id)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold transition-all border text-left flex items-center justify-between cursor-pointer ${
                    categoryId === cat.id
                      ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span className="truncate">{cat.name}</span>
                  {categoryId === cat.id && <Check className="w-3.5 h-3.5 shrink-0 text-white" />}
                </button>
              ))}
            </div>
          </div>

          {/* TAGAR DENGAN SEPARATOR COMMA & REKOMENDASI */}
          <div className="space-y-3 pt-4 border-t border-slate-100">
            <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">6</span>
              <span>Tagar Kata Kunci SEO (#)</span>
              <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-bold text-[10px] rounded-md">
                LSI Keywords
              </span>
            </label>

            {/* Rekomendasi Tagar yang digunakan > 10 kali */}
            {suggestedTags.length > 0 && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1.5">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 block flex items-center gap-1">
                  <TagIcon className="w-3 h-3 text-blue-600 shrink-0" />
                  <span>Tagar Populer (&gt;10x Digunakan - Klik untuk Tambah):</span>
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {suggestedTags
                    .filter((t) => !tags.includes(t))
                    .map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          setTags([...tags, t]);
                          showToast(`Tag #${t} ditambahkan!`, 'success');
                        }}
                        className="px-2.5 py-1 bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-800 border border-slate-200 hover:border-blue-300 rounded-lg text-[11px] font-semibold cursor-pointer transition-all flex items-center gap-1"
                      >
                        <span>#{t}</span>
                        <span className="text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded font-mono font-bold">
                          {tagCounts[t]}x
                        </span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* List tagar aktif dengan URL Link masing-masing */}
            <div className="flex flex-wrap items-center gap-2">
              {tags.map((tag) => {
                const url = tagUrls[tag];
                return (
                  <div
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-800 border border-slate-300 rounded-xl text-xs shadow-2xs group hover:border-emerald-500 transition-all"
                  >
                    <span className="font-extrabold text-emerald-700">#{tag}</span>
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded text-[10px] font-semibold border border-emerald-200 transition-colors max-w-[140px] truncate"
                        title={`Tautan aktif: ${url}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <LinkIcon className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{url.replace(/^https?:\/\//, '')}</span>
                      </a>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">(Default filter)</span>
                    )}

                    <button
                      type="button"
                      onClick={() => setEditingTagUrl({ tag, url: url || '' })}
                      className="text-slate-400 hover:text-blue-600 p-0.5 rounded cursor-pointer transition-colors"
                      title={url ? 'Ubah link URL tagar ini' : 'Tambahkan link URL untuk tagar ini'}
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-red-600 p-0.5 text-slate-400 rounded cursor-pointer transition-colors"
                      title="Hapus tagar"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Inline Editor for Specific Tag URL */}
            {editingTagUrl && (
              <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl space-y-2 animate-in fade-in duration-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-900 flex items-center gap-1.5">
                    <LinkIcon className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Tautkan Link URL ke Tagar #{editingTagUrl.tag}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingTagUrl(null)}
                    className="text-slate-400 hover:text-slate-700 p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editingTagUrl.url}
                    onChange={(e) => setEditingTagUrl({ ...editingTagUrl, url: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSaveTagUrl(editingTagUrl.tag, editingTagUrl.url);
                      }
                    }}
                    placeholder="Contoh: https://bellvatour.com/produk/paket-outbound atau /produk/outbound"
                    className="flex-1 bg-white border border-emerald-300 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveTagUrl(editingTagUrl.tag, editingTagUrl.url)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                  >
                    Simpan Link
                  </button>
                  {editingTagUrl.url && (
                    <button
                      type="button"
                      onClick={() => handleSaveTagUrl(editingTagUrl.tag, '')}
                      className="px-2 py-1.5 bg-white border border-slate-300 hover:bg-rose-50 hover:text-rose-600 text-slate-600 text-xs rounded-lg transition-colors cursor-pointer"
                      title="Hapus URL dari tagar ini"
                    >
                      Hapus URL
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-emerald-800">
                  Saat pembaca mengklik tagar #{editingTagUrl.tag} di halaman baca artikel, tautan di atas akan langsung dibuka.
                </p>
              </div>
            )}

            {/* Input tagar baru beserta URL Link opsional */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 pt-1">
              <div className="sm:col-span-6 relative">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => handleTagInputChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTagManual())}
                  placeholder="Nama tagar (misal: outbound lembang, wisata bandung)"
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="sm:col-span-4 relative">
                <input
                  type="text"
                  value={tagUrlInput}
                  onChange={(e) => setTagUrlInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTagManual())}
                  placeholder="URL link tujuan (opsional: https://...)"
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2 text-xs font-medium text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={handleAddTagManual}
                  className="w-full h-full py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-1 shadow-xs"
                >
                  <TagIcon className="w-3 h-3" />
                  <span>+ Tambah</span>
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 font-semibold italic">
              *Kini setiap tagar dapat memiliki URL Link tujuan. Jika URL diisi, pembaca yang mengklik tagar tersebut akan langsung diarahkan ke link URL yang ditentukan (misal: paket wisata, link WhatsApp, atau brosur). Jika dikosongkan, tagar akan memfilter artikel terkait.
            </p>
          </div>

        </div>

        {/* CARD 4: FOTO SAMPUL UTAMA & MEDIA GALLERY TERUNGGAH */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 space-y-4 shadow-2xs">
          <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">7</span>
            <span>Foto Sampul Utama & Takarir (Caption SEO)</span>
            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-bold text-[10px] rounded-md">
              Image Alt & Caption
            </span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
            <div className="relative aspect-video rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 overflow-hidden flex items-center justify-center shadow-inner">
              {coverImage ? (
                <img
                  src={coverImage}
                  alt="Cover Preview"
                  onError={(e) =>
                    handleImageError(
                      e,
                      article.id,
                      categories.find((c) => c.id === categoryId)?.name || 'Hukum'
                    )
                  }
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="text-center p-3 text-slate-400">
                  <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-50" />
                  <span className="text-[10px] font-bold">Belum Ada Sampul</span>
                </div>
              )}
            </div>

            <div className="md:col-span-2 space-y-4">
              {/* Media Picker Toggle */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowMediaGallery(!showMediaGallery)}
                  className="w-full py-2 px-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-800 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors cursor-pointer"
                >
                  <Layers className="w-4 h-4 text-blue-600" />
                  <span>{showMediaGallery ? 'Sembunyikan Galeri Media' : '🖼️ Pilih Dari Foto Yang Sudah Pernah Upload'}</span>
                </button>

                {/* Grid foto yang pernah diupload */}
                {showMediaGallery && (
                  <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2 animate-in slide-in-from-top-2 duration-200">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 block">
                      Klik salah satu gambar untuk dijadikan foto sampul artikel:
                    </span>
                    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 max-h-[160px] overflow-y-auto pr-1">
                      {mediaImages.map((imgUrl, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setCoverImage(imgUrl);
                            showToast('Foto sampul diset dari galeri terunggah!', 'success');
                          }}
                          className={`aspect-video rounded-lg overflow-hidden border bg-white cursor-pointer transition-all relative ${
                            coverImage === imgUrl ? 'ring-3 ring-blue-500 scale-95 border-blue-500' : 'border-slate-200 hover:scale-105'
                          }`}
                        >
                          <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                          {coverImage === imgUrl && (
                            <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                              <Check className="w-4 h-4 text-blue-700 bg-white rounded-full p-0.5 shadow" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 pt-3 space-y-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-700 block mb-1">Unggah Foto Baru (WebP Otomatis):</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    disabled={isUploadingCover}
                    className="block w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-200 file:text-slate-800 hover:file:bg-slate-300 cursor-pointer"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-700 block mb-1">Atau Tempel Link / URL Gambar:</label>
                  <input
                    type="url"
                    placeholder="https://images.unsplash.com/..."
                    value={coverImage}
                    onChange={(e) => setCoverImage(e.target.value)}
                    className="w-full text-xs p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-blue-600"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-700 block mb-1">
                    Keterangan Foto (Caption / Takarir Gambar):
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: Tim profesional memandu jalannya presentasi dan sesi kegiatan"
                    value={imageCaption}
                    onChange={(e) => setImageCaption(e.target.value)}
                    className="w-full text-xs p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-blue-600"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    *Membantu artikel masuk ke fitur Google Image Search & Discover.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER BAR FOR SUBMITTING */}
        <div className="bg-slate-900 text-white rounded-2xl p-4 sm:p-5 shadow-xl border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4 sticky bottom-4 z-20">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <span className="w-7 h-7 rounded-full bg-slate-800 text-white border border-slate-700 text-xs font-black flex items-center justify-center shrink-0">
              8
            </span>
            <div className="flex-1 min-w-0">
              <label className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block mb-0.5">
                Status Penerbitan Artikel:
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                className="bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-700 focus:outline-none focus:border-blue-500 cursor-pointer"
              >
                <option value="published">✓ Terbitkan (Published)</option>
                <option value="draft">📄 Simpan Sebagai Draft</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 text-xs font-bold transition-colors cursor-pointer"
            >
              Batal
            </button>

            <button
              type="button"
              onClick={() => handleSubmit('draft')}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold transition-colors border border-slate-700 cursor-pointer"
            >
              Simpan Draft
            </button>

            <button
              type="button"
              onClick={() => handleSubmit('published')}
              className="flex-1 md:flex-none px-6 py-2.5 rounded-xl text-white font-black text-xs shadow-lg transition-all flex items-center justify-center gap-2 hover:brightness-110 active:scale-98 cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
              <Check className="w-4 h-4" />
              <span>{article.id ? 'Perbarui Artikel' : 'Terbitkan Artikel Sekarang'}</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
